import type { Tool, ToolHandle } from '@toolproof-core/genesis/_contract/_index';

import type { CameraGraph, CameraPath, CameraStation, CameraView } from '@toolproof-core/visualization/_contract/camera';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';
import { collectEntityTargetExtentPoints } from '@toolproof-core/visualization/rendering/_pure/frame-bounds';
import type { WorkflowGrouping } from '@/workflow-runner/_contract/episode';

import { getResourceTypeRingRadius } from '@/workflow/_pure/config/derivationConfig';
import { buildToolStepItemKey, parseWorkflowStateEntryConstellationKey, parseWorkflowStateEntryItemKey } from '@/workflow/_pure/keyCodec';
import { CAMERA_DEFAULTS } from '@/workflow-runner/_data/config/fragments/camera';
import { CAMERA_TRANSITION_DURATION_MS } from '@/workflow-runner/_data/config/config';
import { isNaturalResultSettlementOffsets, resolveOperationPresentationMode } from '@/workflow-runner/_pure/config/config';
import { derivePanelCameraGeometry, toCameraViewId } from '@/workflow-runner/_pure/derivation/cameraGraph';
import { selectActiveToolStepFromTraceSnapshot } from '@/workflow-runner/_pure/model/traceSnapshotSelectors';
import { selectWorkflowCameraDirections } from '@/workflow-runner/_pure/workflowDirectionRules';
import { deriveWorkflowCameraMoment } from '@/workflow-runner/_pure/workflowMoment';
import { deriveOperationCameraBounds } from '@/workflow-runner/_pure/primitiveOperationRod';
import { derivePrimitiveCameraPlan } from '@/workflow-runner/primitiveOperationPlayback';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { WorkflowCameraDirectionEffect } from '@/workflow-runner/_contract/_~generated/cameraDirectionEffect';
import type { Config } from '@/workflow-runner/_contract/config';
import type { PrimitiveCameraPlanCoreDeriver } from '@/workflow-runner/_contract/primitiveCameraPlan';
import type { PrimitiveOperationPendingReplayMove } from '@/workflow-runner/_contract/runtime';
import type { TraceSnapshotView } from '@/workflow-runner/_contract/traceSnapshot';

function isToolStepKey(key: string): key is `TOOL_STEP-${string}` {
    return key.startsWith('TOOL_STEP-');
}

export function withCameraGraph(args: {
    frame: SpaceDerivationFrame;
    config: Config;
    traceSnapshot: TraceSnapshotView | undefined;
    activeSnapshot?: TraceSnapshotView;
    toolMap: ReadonlyMap<ToolHandle, Tool>;
    pendingReplayMove?: PrimitiveOperationPendingReplayMove;
    /** The final replay position is settled, with no operation or structural move pending. */
    replayComplete?: boolean;
    workflowStateEntryGap?: number;
    deriveCameraPlanCore?: PrimitiveCameraPlanCoreDeriver;
    directionCatalog?: DirectionCatalog<WorkflowCameraDirectionEffect>;
    grouping?: WorkflowGrouping;
}): SpaceDerivationFrame {
    const settings = args.config.adapterModeConfig.camera ?? {
        ...CAMERA_DEFAULTS, navigationDurationMs: CAMERA_TRANSITION_DURATION_MS,
    };
    if (settings.navigationMode !== 'free' && settings.navigationMode !== 'guided') {
        throw new Error('Runner camera navigationMode must be free or guided.');
    }
    if (![settings.foldedElevation, settings.operationElevation].every((value) => Number.isFinite(value) && value > 0)
        || !Number.isFinite(settings.operationAngleDegrees)
        || !Number.isFinite(settings.panelsElevation) || settings.panelsElevation <= 0
        || !Number.isFinite(settings.autoplayGlideFraction ?? 0)
        || (settings.autoplayGlideFraction ?? 0) < 0 || (settings.autoplayGlideFraction ?? 0) > 0.25
        || !Array.isArray(settings.naturalLessThanInspectionOffset)
        || settings.naturalLessThanInspectionOffset.length !== 3
        || !settings.naturalLessThanInspectionOffset.every(Number.isFinite)
        || Math.hypot(...settings.naturalLessThanInspectionOffset) === 0
        || !isNaturalResultSettlementOffsets(settings.naturalResultSettlementOffsets)
        || !Number.isFinite(settings.operationPadding) || settings.operationPadding < 1
        || !Number.isFinite(settings.fov) || settings.fov <= 0 || settings.fov >= 180
        || !Number.isFinite(settings.navigationDurationMs) || settings.navigationDurationMs < 0) {
        throw new Error('Invalid runner camera geometry, FOV, padding or duration.');
    }
    const frame = args.frame;
    const views: Record<string, CameraView> = {};
    const stations: Record<string, CameraStation> = {};
    const activeStep = selectActiveToolStepFromTraceSnapshot(args.activeSnapshot ?? args.traceSnapshot);
    const directions = selectWorkflowCameraDirections(
        deriveWorkflowCameraMoment(args.activeSnapshot ?? args.traceSnapshot), args.directionCatalog,
    );
    const panelPresentation = resolveOperationPresentationMode(args.config) === 'panels';
    const advanceBetweenSteps = directions.focus?.panelStep === 'next-input' && panelPresentation && settings.navigationMode === 'guided'
        && args.pendingReplayMove === undefined;
    const snapshot = args.activeSnapshot ?? args.traceSnapshot;
    const nextTick = advanceBetweenSteps && activeStep && snapshot?.tickTracePoints.filter((tick) =>
        tick.tickKind === 'tool_execution' && tick.stepKind === 'tool')[snapshot.position.appliedReplayPositionIndex];
    const nextStepKey = nextTick && isToolStepKey(nextTick.stepKey) && (snapshot?.macroStepControl.branch.replay.runtimeToBranchToolStepKey.get(nextTick.stepKey)
        ?? nextTick.stepKey);
    const nextStep = nextStepKey && args.traceSnapshot?.position.orderedToolSteps.find((step) => step.stepKey === nextStepKey);
    const cameraStep = directions.focus ? nextStep || activeStep : undefined;
    let activeView: string | undefined;
    let choreographing = false;
    for (const step of args.traceSnapshot?.position.orderedToolSteps ?? []) {
        const ringItemKey = buildToolStepItemKey(step.stepKey);
        const ringConstellation = frame.constellations.find((entry) => entry.constellationKind === 'entity'
            && frame.itemCollections.some((collection) => collection.itemCollectionKey === entry.itemCollectionKey
                && collection.items.some((item) => item.itemKey === ringItemKey)));
        if (!ringConstellation || ringConstellation.constellationKind !== 'entity') continue;
        const layout = frame.constellationParametricState?.entityConstellationByKey?.[ringConstellation.constellationKey]?.layout;
        if (layout?.layoutKind !== 'concentric-ring') continue;
        const collection = frame.itemCollections.find((entry) => entry.itemCollectionKey === ringConstellation.itemCollectionKey);
        const index = collection?.items.findIndex((item) => item.itemKey === ringItemKey) ?? -1;
        if (index < 0) continue;
        const radius = layout.ringRadii?.[index] ?? layout.startRadius + index * layout.ringGap;
        const previousRadius = index === 0 ? getResourceTypeRingRadius(args.config)
            : layout.ringRadii?.[index - 1] ?? layout.startRadius + (index - 1) * layout.ringGap;
        const inputGroups = frame.constellations.flatMap((entry) => {
            if (entry.constellationKind !== 'entity') return [];
            const parsed = parseWorkflowStateEntryConstellationKey(entry.constellationKey);
            if (parsed?.stepKey !== step.stepKey || parsed.direction !== 'input') return [];
            const spoke = frame.constellationParametricState?.entityConstellationByKey?.[entry.constellationKey]?.layout;
            if (spoke?.layoutKind !== 'spoke' || spoke.anchor.anchorKind !== 'parametric'
                || spoke.anchor.anglePolicy.anglePolicyKind !== 'explicit') {
                throw new Error(`Workflow camera requires an explicit input radial for ${step.stepKey}.`);
            }
            return [{ resourceType: parsed.resourceTypeHandle, angle: spoke.anchor.anglePolicy.angle, y: spoke.anchor.yOffset ?? 0 }];
        }).sort((a, b) => a.resourceType.localeCompare(b.resourceType));
        const tool = args.toolMap.get(step.toolHandle);
        const firstInputType = tool && Object.values(tool.roleSpec.inputRoleByName)[0]?.resourceTypeHandle;
        const panelMode = panelPresentation;
        const outputType = advanceBetweenSteps && !nextStep && step.stepKey === activeStep?.stepKey ? frame.itemCollections.flatMap((entry) =>
            entry.itemCollectionKind === 'entity' ? entry.items : []).flatMap((item) => {
            const parsed = parseWorkflowStateEntryItemKey(item.itemKey);
            return parsed?.stepKey === step.stepKey && parsed.direction === 'output' ? [parsed.resourceTypeHandle] : [];
        })[0] : undefined;
        // Navigate directly to the next input, or stay at the completed output until its topology exists.
        const preferredType = outputType ?? inputGroups.find((group) => group.resourceType === firstInputType)?.resourceType
            ?? inputGroups[0]?.resourceType;
        const panelGroups = panelMode ? frame.constellations.flatMap((entry) => {
            if (entry.constellationKind !== 'entity') return [];
            const parsed = parseWorkflowStateEntryConstellationKey(entry.constellationKey);
            if (parsed?.stepKey !== step.stepKey) return [];
            const spoke = frame.constellationParametricState?.entityConstellationByKey?.[entry.constellationKey]?.layout;
            if (spoke?.layoutKind !== 'spoke' || spoke.anchor.anchorKind !== 'parametric'
                || spoke.anchor.anglePolicy.anglePolicyKind !== 'explicit') return [];
            return [{ resourceType: parsed.resourceTypeHandle, angle: spoke.anchor.anglePolicy.angle, y: spoke.anchor.yOffset ?? 0 }];
        }).filter((group, index, groups) => groups.findIndex((other) => other.resourceType === group.resourceType) === index)
            : inputGroups;
        for (const group of panelGroups) {
            const { angle, y, resourceType } = group;
            const target: [number, number, number] = [Math.cos(angle) * radius, y, Math.sin(angle) * radius];
            const folded = toCameraViewId(step.stepKey, resourceType, 'folded');
            const operation = toCameraViewId(step.stepKey, resourceType, 'operation');
            const position: [number, number, number] = [
                Math.cos(angle) * previousRadius, y + (panelMode ? settings.panelsElevation : settings.foldedElevation),
                Math.sin(angle) * previousRadius,
            ];
            stations[folded] = { stationKind: 'explicit', position,
                fov: settings.fov,
            };
            const panelBounds = panelMode ? derivePanelCameraGeometry(frame, step.stepKey, undefined, resourceType) : undefined;
            if (panelBounds) {
                stations[folded] = { stationKind: 'framing', fov: settings.fov,
                    positioningAnchor: { anchorKind: 'position', position },
                    framing: { framingKind: 'extent', bounds: panelBounds, padding: settings.operationPadding,
                        direction: [-Math.cos(angle) * (radius - previousRadius), settings.panelsElevation,
                            -Math.sin(angle) * (radius - previousRadius)] } };
            }
            views[folded] = { station: folded, target: { anchorKind: 'position', position: panelBounds?.center ?? target } };
            if (panelBounds && (settings.autoplayGlideFraction ?? 0) > 0) {
                const glide = toCameraViewId(step.stepKey, resourceType, 'glide');
                const drift = layout.ringGap * (settings.autoplayGlideFraction ?? 0);
                const glidePosition: [number, number, number] = [
                    position[0] + Math.cos(angle) * drift, position[1], position[2] + Math.sin(angle) * drift,
                ];
                stations[glide] = { stationKind: 'framing', fov: settings.fov,
                    positioningAnchor: { anchorKind: 'position', position: glidePosition },
                    framing: { framingKind: 'extent', bounds: panelBounds, padding: settings.operationPadding,
                        direction: [-Math.cos(angle) * (radius - previousRadius), settings.panelsElevation,
                            -Math.sin(angle) * (radius - previousRadius)] } };
                views[glide] = { station: glide, target: views[folded].target };
            }
            const offsetAngle = angle + settings.operationAngleDegrees * Math.PI / 180;
            const direction: [number, number, number] = [
                radius * (Math.cos(offsetAngle) - Math.cos(angle)),
                settings.operationElevation,
                radius * (Math.sin(offsetAngle) - Math.sin(angle)),
            ];
            const bounds = settings.navigationMode === 'guided' && resolveOperationPresentationMode(args.config) === 'wheels' ? deriveOperationCameraBounds({
                frame, step, config: args.config, toolMap: args.toolMap, target,
                workflowState: args.traceSnapshot?.position.workflowState ?? {},
            }) : undefined;
            if (bounds) {
                stations[operation] = { stationKind: 'framing', framing: {
                    bounds, direction, padding: settings.operationPadding,
                }, fov: settings.fov };
                views[operation] = { station: operation, target: { anchorKind: 'position', position: target } };
            }
            if (step.stepKey === cameraStep?.stepKey && resourceType === preferredType) {
                activeView = directions.focus?.view === 'operation-or-folded'
                    && resolveOperationPresentationMode(args.config) === 'wheels' && bounds !== undefined
                    ? operation : folded;
                if (activeView === operation && settings.navigationMode === 'guided') {
                    const pendingStepIndex = args.pendingReplayMove?.arithmeticProgress.pendingStepIndex ?? null;
                    const stepIndex = pendingStepIndex
                        ?? args.pendingReplayMove?.arithmeticProgress.committedStepIndex ?? 0;
                    const plan = derivePrimitiveCameraPlan({
                        frame, step, config: args.config, toolMap: args.toolMap,
                        workflowState: args.traceSnapshot?.position.workflowState ?? {},
                        ...(args.activeSnapshot ? { postWorkflowState: args.activeSnapshot.position.workflowState } : {}),
                        scope: `${args.traceSnapshot?.selectedTraceHandle}:${step.stepKey}`,
                        stepIndex, pending: pendingStepIndex !== null, workflowStateEntryGap: args.workflowStateEntryGap ?? 1,
                        ...(args.deriveCameraPlanCore ? { deriveCore: args.deriveCameraPlanCore } : {}),
                    });
                    if (plan && (plan.activeView || plan.choreography)) {
                        Object.assign(stations, plan.graph.stations);
                        Object.assign(views, plan.graph.views);
                        activeView = plan.activeView;
                        // A playing choreographed step navigates itself; no destination is declared meanwhile.
                        choreographing = plan.activeView === undefined;
                    }
                }
            }
        }
        if (step.stepKey === cameraStep?.stepKey && inputGroups.length === 0) {
            const id = toCameraViewId(step.stepKey, 'tool', 'folded');
            stations[id] = { stationKind: 'anchored',
                anchor: { anchorKind: 'parametric', parametricKind: 'ring-intersection', ringItemKey,
                    anglePolicy: { anglePolicyKind: 'explicit', angle: Math.PI / 2 } },
                offset: [0, settings.foldedElevation, settings.foldedElevation],
                fov: settings.fov,
            };
            views[id] = { station: id, target: stations[id].anchor };
            activeView = id;
        }
    }
    if (args.grouping === 'cases' || args.grouping === 'macroSteps') {
        const suffix = args.grouping === 'cases' ? ':case-torus:' : ':macro-step-torus:';
        const keys = new Set(frame.itemCollections.flatMap(collection =>
            collection.itemCollectionKind === 'entity' && collection.itemCollectionKey.includes(suffix)
                ? collection.items.map(item => item.itemKey) : []));
        const points = collectEntityTargetExtentPoints(frame, keys);
        if (points.length === 0) throw new Error('Workflow grouping camera requires visible envelopes.');
        activeView = `grouping:${args.traceSnapshot?.selectedTraceHandle}:${args.traceSnapshot?.position.appliedReplayPositionIndex}:${args.grouping}`;
        stations[activeView] = { stationKind: 'framing', framing: { framingKind: 'extent',
            bounds: { center: [0, 0, 0], points }, direction: [0, 0.28, 1], padding: 1.5 }, fov: 60 };
        views[activeView] = { station: activeView, target: { anchorKind: 'position', position: [0, 0, 0] } };
    }
    // Publish the explicit ingress edges needed by this replay destination, not a quadratic all-pairs graph.
    const paths: CameraPath[] = activeView === undefined ? [] : Object.keys(views)
        .filter((from) => from !== activeView).map((from) => ({
            pathId: `${from}->${activeView}`, from, to: activeView, pathKind: 'straight',
        }));
    const graph: CameraGraph = { stations, views, paths };
    const destination = settings.navigationMode === 'guided' && args.replayComplete && directions.completion?.view === 'overview' ? 'overview'
        : activeView ?? (directions.focus && args.traceSnapshot && !choreographing ? 'overview' : undefined);
    return {
        ...frame,
        spaceRenderOptions: { ...frame.spaceRenderOptions, camera: {
            ...frame.spaceRenderOptions?.camera, graph,
            navigationMode: settings.navigationMode,
            navigationDurationMs: settings.navigationDurationMs,
            navigationOrbitCenter: directions.focus?.orbit === 'origin-for-panels' && panelPresentation && destination !== 'overview'
                ? [0, 0, 0] : null,
            activeView: destination,
        } },
    };
}
